import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { QueryRef } from 'apollo-angular';

import { AuthService } from '@core/services/auth/auth.service';
import { NavigationMenuItem, NavigationService } from '@core/services/navigation.service';
import { MeGQL, MeQuery } from '@generated/graphql';
import {
  PalantirSideMenuComponent,
  SideMenuItem,
  SideMenuUser,
  SideMenuCompany,
} from '@flux/design-system';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';
import { SidebarService } from './sidebar.service';
import { PermissionsService } from '../../../core/services/permissions.service.js';
import { FeatureFlagService } from '../../../core/services/feature-flag/feature-flag.service.js';
import { FeatureFlagsEnum } from '../../../../generated/featureFlags.enum.js';
import { PermissionsEnum } from '../../../../generated/permissions.enum.js';
import { BlogNotificationService } from '../../../core/services/blog-notification/blog-notification.service.js';

@Component({
  selector: 'fx-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PalantirSideMenuComponent],
  providers: [SidebarService],
})
export class SidebarComponent implements OnInit, OnDestroy {
  public readonly blogNotificationService = inject(BlogNotificationService);

  private readonly user = signal<MeQuery['me'] | null>(null);
  private readonly notificationsCount = signal(0);
  private queryRef!: QueryRef<MeQuery>;

  public isCollapsed = this.sidebarService.collapsed;

  /**
   * User info for the side menu
   */
  public sideMenuUser = computed<SideMenuUser>(() => {
    const user = this.user();
    return {
      name: user?.name ?? '',
      email: user?.email ?? '',
      image: user?.avatarUrl ?? 'assets/favicon.svg',
    };
  });

  /**
   * Company info for the side menu (hardcoded to Flux Control)
   */
  public sideMenuCompany = computed<SideMenuCompany>(() => ({
    name: '',
    image: {
      expanded: 'assets/logo/flux-logo.svg',
      collapsed: 'assets/favicon.svg',
    },
  }));

  /**
   * Notification count for the side menu badge
   */
  public notificationCount = computed(() => this.notificationsCount());

  /**
   * Menu items filtered by permissions and feature flags, with badges applied
   */
  public menuItems = computed<SideMenuItem[]>(() => {
    const navItems = this.navigationService.getNavigationItems();
    const notificationCount = this.notificationsCount();
    const hasUnreadBadges = this.mySpaceBadgesService.hasUnreadBadges();

    // Recursively filter and transform navigation items
    return this.processNavigationItems(navItems, notificationCount, hasUnreadBadges);
  });

  constructor(
    private readonly sidebarService: SidebarService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
    private readonly navigationService: NavigationService,
    private readonly permissionsService: PermissionsService,
    private readonly featureFlagsService: FeatureFlagService,
    private readonly mySpaceBadgesService: MySpaceBadgesService,
    private readonly meGQL: MeGQL,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.sidebarService.notificationsCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.notificationsCount.set(value);
      });

    this.fetchUser();
  }

  ngOnDestroy(): void {
    if (this.queryRef) {
      this.queryRef.stopPolling();
    }
  }

  /**
   * Handle menu item click - navigate to the item's URL
   */
  public onMenuItemClick(item: SideMenuItem): void {
    if (item.url) {
      this.router.navigate([item.url]);
    }
  }

  /**
   * Handle user action click (logout, profile)
   */
  public onUserAction(action: string): void {
    switch (action) {
      case 'logout':
        this.authService.logout();
        break;
      case 'profile':
        this.router.navigate(['perfil']);
        break;
    }
  }

  public onHelpButtonClick(): void {
    window.open('/wiki/index.html', '_blank');
  }

  public onNotificationButtonClick(): void {
    this.router.navigate(['notificacoes']);
  }

  public onRocketButtonClick(): void {
    this.blogNotificationService.openManuallyWithAllPosts();
  }

  /**
   * Toggle sidebar collapsed state
   */
  public onToggleMenu(): void {
    this.sidebarService.toggleCollapse();
  }

  /**
   * Recursively process navigation items: filter by permissions and apply badges
   */
  private processNavigationItems(
    items: NavigationMenuItem[],
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem[] {
    return items.map((item) => this.transformItem(item, notificationCount, hasUnreadBadges));
  }

  /**
   * Check if a navigation item is visible based on permissions and feature flags
   */
  private isItemVisible(item: NavigationMenuItem): boolean {
    // Check feature flags
    if (item.has_feature_flag?.length) {
      const hasFlag = item.has_feature_flag.some((flag) =>
        this.featureFlagsService.isFeatureFlagEnabled(flag as FeatureFlagsEnum),
      );
      if (!hasFlag) {
        return false;
      }
    }

    // Check permissions (any of the required permissions)
    if (item.required_permissions?.length) {
      const hasPermission = item.required_permissions.some((permission) =>
        this.permissionsService.hasPermission(permission as PermissionsEnum),
      );
      if (!hasPermission) {
        return false;
      }
    }

    return true;
  }

  /**
   * Transform a NavigationMenuItem to SideMenuItem, handling children recursively
   */
  private transformItem(
    item: NavigationMenuItem,
    notificationCount: number,
    hasUnreadBadges: boolean,
  ): SideMenuItem {
    // Create base menu item
    const menuItem: SideMenuItem = {
      id: item.id,
      title: item.title,
      url: item.url,
      hidden: !this.isItemVisible(item),
      icon: item.icon,
      withDivider: item.withDivider,
      dontAppearOnClosed: item.dontAppearOnClosed,
    };

    // Recursively process children if present
    if (item.children?.length) {
      const filteredChildren = this.processNavigationItems(
        item.children,
        notificationCount,
        hasUnreadBadges,
      );
      // Only include children array if there are visible items
      if (filteredChildren.length > 0) {
        menuItem.children = filteredChildren;
      }
    }

    // Apply badges for specific items
    if (item.id === 'notificacoes' && notificationCount > 0) {
      menuItem.badge = {
        title: notificationCount > 99 ? '99+' : notificationCount,
        bg: 'var(--flux-blue-500)',
      };
    }

    if (item.id === 'meu-espaco' && hasUnreadBadges) {
      menuItem.badge = {
        title: '●',
        bg: 'var(--flux-blue-500)',
      };
    }

    return menuItem;
  }

  private fetchUser(): void {
    this.queryRef = this.meGQL.watch();

    this.queryRef.valueChanges
      .pipe(
        map((result) => result.data.me),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.user.set(data);
      });
  }
}
